import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AcceptInviteResult,
  InviteDetails,
  acceptInvite,
  declineInvite,
  getInviteDetails,
} from "../api/members";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { handleError } from "../lib/errors";
import { notify } from "../lib/notify";
import { useAppStore } from "../store/useAppStore";

export default function AcceptInvitePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, user, setToken, setUser } = useAppStore();

  const [details, setDetails] = useState<InviteDetails | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Registration fields — only used when the visitor is not already logged in.
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  useEffect(() => {
    if (!token) return;
    getInviteDetails(token)
      .then((d) => {
        setDetails(d);
        // Pre-fill email if we have invite details
        if (d.email) setEmail(d.email);
      })
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 410) {
          setLoadError("This invitation has expired. Ask the project owner to resend it.");
        } else {
          setLoadError("This invitation is not valid or has already been used.");
        }
      })
      .finally(() => setLoading(false));
  }, [token]);

  const handleAccept = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setSubmitting(true);

    try {
      // Authenticated users don't need to supply registration fields.
      const body = isAuthenticated ? undefined : { name, email, password };
      const result: AcceptInviteResult = await acceptInvite(token, body);

      // If a new account was created (token returned), log the user in.
      if (result.token && result.user) {
        setToken(result.token);
        setUser(result.user as any);
        localStorage.setItem("token", result.token);
      }

      notify.success("Welcome!", {
        description: `You now have ${result.role} access to the project.`,
      });
      navigate(`/projects/${result.projectId}`);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 410) {
        notify.error("This invitation has expired.");
      } else if (status === 403 && err?.response?.data?.code === "EMAIL_MISMATCH") {
        notify.error(
          "This invite was sent to a different address. Sign in with that address and try again."
        );
      } else {
        handleError(err, { scope: "AcceptInvitePage" });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = async () => {
    if (!token) return;
    try {
      await declineInvite(token);
      notify.success("Invitation declined.");
      navigate(isAuthenticated ? "/projects" : "/auth/login");
    } catch (err) {
      handleError(err, { scope: "AcceptInvitePage.decline" });
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <p className="text-muted-foreground">Loading invitation…</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Invitation not found</CardTitle>
            <CardDescription>{loadError}</CardDescription>
          </CardHeader>
          <CardFooter>
            <Button variant="outline" onClick={() => navigate("/projects")}>
              Go to projects
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center gap-2">
            <CardTitle>Project invitation</CardTitle>
            <Badge variant="secondary">{details?.role}</Badge>
          </div>
          <CardDescription>
            <strong>{details?.invitedByName}</strong> has invited you to{" "}
            <strong>{details?.projectName}</strong> as a{" "}
            <strong>{details?.role}</strong>.
          </CardDescription>
        </CardHeader>

        <form onSubmit={handleAccept}>
          <CardContent className="space-y-4">
            {/* Registration fields — only shown to unauthenticated visitors.
                Authenticated users accept with a single click (no re-auth needed). */}
            {!isAuthenticated ? (
              <>
                <p className="text-sm text-muted-foreground">
                  Create an account to accept this invitation.
                </p>
                <div className="space-y-2">
                  <Label htmlFor="invite-name">Your name</Label>
                  <Input
                    id="invite-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Full name"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="invite-email">Email</Label>
                  <Input
                    id="invite-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    readOnly={!!details?.email}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="invite-password">Password</Label>
                  <Input
                    id="invite-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Choose a password"
                    required
                    minLength={8}
                  />
                </div>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                You are signed in as <strong>{user?.email}</strong>. Click accept
                to join the project.
              </p>
            )}
          </CardContent>

          <CardFooter className="flex justify-between">
            <Button
              type="button"
              variant="outline"
              onClick={handleDecline}
              disabled={submitting}
            >
              Decline
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting
                ? "Accepting…"
                : isAuthenticated
                ? "Accept invitation"
                : "Create account & accept"}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
